import React, { useCallback, useEffect, useMemo, useState } from "react";
import { fetchCallTasks, markCallDone } from "../../../api/callTasksService";
import { openPopup } from "../../Common/popupUIStore";
import "./callTasks.scss";

// „De sunat” (cererea managerilor, 19.10): elevii de sunat după lecții — din alte școli după prima și după ultima
// lecție (dacă nu mai au nimic programat), ai noștri după a 30-a lecție. Lista o face serverul (call-tasks).

const RESULTS = [
   { value: "CONTINUA", label: "Continuă" },
   { value: "NU_CONTINUA", label: "Nu continuă" },
   { value: "NU_RASPUNDE", label: "Nu răspunde" },
];
const RESULT_LABEL = Object.fromEntries(RESULTS.map((r) => [r.value, r.label]));

const fmt = new Intl.DateTimeFormat("ro-RO", { timeZone: "UTC", day: "2-digit", month: "2-digit", year: "numeric", hour: "2-digit", minute: "2-digit", hour12: false });
const fmtDone = new Intl.DateTimeFormat("ro-RO", { timeZone: "Europe/Chisinau", day: "2-digit", month: "2-digit", hour: "2-digit", minute: "2-digit", hour12: false });
/** Orele lecțiilor sunt „de perete” scrise ca UTC — se afișează ca atare. */
const lessonTime = (iso) => (iso ? fmt.format(new Date(iso)).replace(",", "") : "—");

function TaskRow({ task, onSaved }) {
   const [open, setOpen] = useState(false);
   const [result, setResult] = useState("");
   const [note, setNote] = useState("");
   const [busy, setBusy] = useState(false);
   const [error, setError] = useState("");
   const s = task.student || {};

   const save = async () => {
      if (!result) {
         setError("Alege rezultatul apelului.");
         return;
      }
      setBusy(true);
      setError("");
      try {
         await markCallDone(task.id, result, note.trim());
         onSaved?.(task.id);
      } catch (e) {
         setError(e?.message || "Nu am putut salva apelul.");
      } finally {
         setBusy(false);
      }
   };

   return (
      <div className="callTasks__row" data-task-id={task.id} data-reason={task.reason}>
         <div className="callTasks__main">
            <button type="button" className="callTasks__name" onClick={() => openPopup("studentDetails", { student: { id: s.id, firstName: s.name } })}>
               {s.name}
            </button>
            {s.phone ? (
               <a className="callTasks__phone" href={`tel:${s.phone}`}>
                  {s.phone}
               </a>
            ) : (
               <span className="callTasks__phone is-none">fără telefon</span>
            )}
            <div className="callTasks__reason">{task.reasonLabel}</div>
            <div className="callTasks__meta">
               Lecția: {lessonTime(task.lesson?.startTime)}
               {task.lesson?.instructor ? ` · ${task.lesson.instructor}` : ""} · lecții făcute: {task.lessonsDone}
               {" · "}
               {task.nextLesson ? `următoarea: ${lessonTime(task.nextLesson)}` : "nimic programat"}
               {s.group ? ` · ${s.group}` : ""}
               {s.isGuest ? " · oaspete" : ""}
            </div>
            {task.status !== "OPEN" ? (
               <div className="callTasks__done">
                  {task.status === "AUTO_CLOSED"
                     ? "S-a programat între timp (închis automat)"
                     : `${RESULT_LABEL[task.result] || task.result}${task.note ? ` — ${task.note}` : ""}`}
                  {task.doneAt ? ` · ${fmtDone.format(new Date(task.doneAt))}` : ""}
                  {task.doneBy ? ` · ${task.doneBy}` : ""}
               </div>
            ) : null}
         </div>
         {task.status === "OPEN" ? (
            <div className="callTasks__actions">
               {!open ? (
                  <button type="button" className="callTasks__btn is-primary" onClick={() => setOpen(true)}>
                     Am sunat
                  </button>
               ) : (
                  <div className="callTasks__form">
                     <div className="callTasks__results" role="radiogroup" aria-label="Rezultatul apelului">
                        {RESULTS.map((r) => (
                           <button
                              key={r.value}
                              type="button"
                              role="radio"
                              aria-checked={result === r.value}
                              className={`callTasks__btn${result === r.value ? " is-active" : ""}`}
                              onClick={() => setResult(r.value)}
                           >
                              {r.label}
                           </button>
                        ))}
                     </div>
                     <textarea
                        className="callTasks__note"
                        placeholder="Notiță (opțional)"
                        maxLength={500}
                        value={note}
                        onChange={(e) => setNote(e.target.value)}
                     />
                     <div className="callTasks__formBtns">
                        <button type="button" className="callTasks__btn is-primary" onClick={save} disabled={busy}>
                           {busy ? "Se salvează…" : "Salvează"}
                        </button>
                        <button type="button" className="callTasks__btn is-quiet" onClick={() => setOpen(false)} disabled={busy}>
                           Renunță
                        </button>
                     </div>
                     {error ? <div className="callTasks__error">{error}</div> : null}
                  </div>
               )}
            </div>
         ) : null}
      </div>
   );
}

export default function CallTasksPage() {
   const [tab, setTab] = useState("OPEN");
   const [lists, setLists] = useState({ OPEN: null, DONE: null });
   const [error, setError] = useState("");

   const load = useCallback(async (status) => {
      setError("");
      try {
         const res = await fetchCallTasks(status);
         setLists((p) => ({ ...p, [status]: Array.isArray(res?.tasks) ? res.tasks : [] }));
      } catch (e) {
         setError(e?.message || "Nu am putut încărca lista.");
      }
   }, []);

   useEffect(() => {
      load("OPEN");
   }, [load]);
   useEffect(() => {
      if (tab === "DONE" && lists.DONE === null) load("DONE");
   }, [tab, lists.DONE, load]);

   const onSaved = useCallback(
      (id) => {
         setLists((p) => ({ ...p, OPEN: (p.OPEN || []).filter((t) => t.id !== id), DONE: null }));
      },
      [],
   );

   const items = lists[tab];
   const openCount = lists.OPEN?.length ?? 0;
   const grouped = useMemo(() => items || [], [items]);

   return (
      <main className="main">
         <section className="callTasks page">
            <div className="callTasks__header">
               <h2 className="callTasks__title">De sunat</h2>
               <div className="callTasks__tabs" role="tablist">
                  <button type="button" role="tab" aria-selected={tab === "OPEN"} className={`callTasks__tab${tab === "OPEN" ? " is-active" : ""}`} onClick={() => setTab("OPEN")}>
                     De sunat{lists.OPEN ? ` (${openCount})` : ""}
                  </button>
                  <button type="button" role="tab" aria-selected={tab === "DONE"} className={`callTasks__tab${tab === "DONE" ? " is-active" : ""}`} onClick={() => setTab("DONE")}>
                     Sunați
                  </button>
                  <button type="button" className="callTasks__tab is-quiet" onClick={() => load(tab)} title="Reîncarcă">
                     ↻
                  </button>
               </div>
            </div>
            <p className="callTasks__hint">
               Din alte școli (bifa „Din altă școală” din fișa elevului) — după prima lecție și după ultima, dacă nu mai are nimic
               programat; elevii noștri — după a 30-a lecție. Lista se completează singură la fiecare 30 de minute.
            </p>
            {error ? <div className="callTasks__error">{error}</div> : null}
            {items === null ? <div className="callTasks__empty">Se încarcă…</div> : null}
            {items && items.length === 0 ? (
               <div className="callTasks__empty">{tab === "OPEN" ? "Nu e nimeni de sunat acum." : "Încă nu e niciun apel."}</div>
            ) : null}
            <div className="callTasks__list">
               {grouped.map((t) => (
                  <TaskRow key={t.id} task={t} onSaved={onSaved} />
               ))}
            </div>
         </section>
      </main>
   );
}
